import { useEffect, useMemo, useState } from 'react'
import { useParams, useSearchParams } from 'react-router-dom'
import sanitizeHtml from 'sanitize-html'
import { CircularProgressContinuousSized } from '@/components/loaders/CircularProgressContinuous'
import InlineTipError from '@/components/inline-tips/InlineTipError'
import StandaloneReferenceViewCaselawTabs from './StandaloneReferenceViewCaselawTabs'
import { useTitle } from '@/hooks/use-title'
import { brandedAIFriendlyName, getHelpCenterUrl } from '@/util/enterprise'
import { useCaselawMetadataInStandalone } from '../hooks/useCaselawMetadataInStandalone'
import { useGetCaselawReferenceHtmlInStandalone } from '../hooks/useGetCaselawReferenceHtmlInStandalone'
import { buildChatReferenceFromStandaloneMetadata, createRiaContext, trackRiaSourceDownload } from '@/analytics/utils/ria-source-analytics'
import { RiaSourceTriggerLocation } from '@/analytics/enums/ria.enums'
import { isValidCaselawIdentifierType } from '@/common/types/caselaw-identifiers'
import { useAnalytics } from '@/analytics/hooks/useAnalytics'
import { AnalyticsEvent } from '@/analytics/schema/events.schema'
import { downloadReferenceAsDocX } from '@/chat-common/util/download-conversation'
import { kSanitizationAllowedTags } from '@/constants/constants-ui'
import { Button } from '@/components/button/Button'
import { FileDownload, OpenNew } from '@/assets/icons'
import { Link } from 'react-router-dom'
import { useAppSelector } from '@/store/store-hooks'
import { RootState } from '@/store/store'
import { getCaseStatus } from '@/citator/fetch/get-case-status'
import { CaseStatusInfoTag } from '@/citator/components/CaseStatusInfoTag'
import { isDisplayableCaseStatus } from '@/citator/schema/case-status-schema'

const CITATOR_HELP_CENTER_URL = getHelpCenterUrl('legal-research/ai-citator')

/**
 * Reference Viewer Case Law Page
 * Displays the source viewer and citator tabs for case law references.
 * Fetches metadata and source HTML, then composes the header (title, citation,
 * Download button, Run Citator button) and the Source View / Citator tabs.
 */
export default function StandaloneReferenceViewerCaseLawPage() {
  const { idType, id, matterId: matterIdPathParam } = useParams<{ idType: string; id: string; matterId?: string }>()
  const [searchParams] = useSearchParams()
  const { trackEvent } = useAnalytics()
  const [downloadLoading, setDownloadLoading] = useState(false)

  const isUnsupportedIdType = idType !== undefined && !isValidCaselawIdentifierType(idType)

  // Only fetch metadata if idType is valid (skip for deprecated parent_id URLs, etc.)
  const { data, isLoading, isError, refetch } = useCaselawMetadataInStandalone(
    isUnsupportedIdType ? 's3_path' : idType ?? '',
    isUnsupportedIdType ? 'unsupported' : id ?? ''
  )

  // Fetch the source HTML once metadata is available. The hook waits idle while
  // metadata is null/undefined, so it's safe to call unconditionally.
  const { data: dataHtml, isLoading: isLoadingHtml, isError: isErrorHtml, refetch: refetchHtml } = useGetCaselawReferenceHtmlInStandalone(data)

  // Sanitize HTML once — shared by the download handler and the tabs renderer.
  const sanitizedHtml = useMemo(
    () =>
      dataHtml
        ? sanitizeHtml(dataHtml, {
            allowedTags: kSanitizationAllowedTags,
            transformTags: { pre: 'p', code: 'p' },
          })
        : null,
    [dataHtml]
  )

  // Get RIA context from URL params for analytics tracking
  const conversationIdParam = searchParams.get('conversationId')
  const messageIdParam = searchParams.get('messageId')
  // matterId comes from the URL path for matter-scoped routes; fall back to search param for old global URLs
  const matterIdParam = matterIdPathParam ?? searchParams.get('matterId')

  const analyticsContext = createRiaContext(conversationIdParam, messageIdParam, matterIdParam)

  // Build once — used by the download handler and the citator button.
  const analyticsReference = useMemo(
    () => (data ? buildChatReferenceFromStandaloneMetadata(data as unknown as Record<string, unknown>, data.parent_id) : null),
    [data]
  )

  const handleDownload = () => {
    if (!data || !sanitizedHtml) return

    if (analyticsContext && analyticsReference) {
      trackRiaSourceDownload({
        trackEvent,
        context: analyticsContext,
        reference: analyticsReference,
        triggerLocation: RiaSourceTriggerLocation.StandaloneViewerHeader,
      })
    }
    trackEvent(AnalyticsEvent.DownloadChatSourceToDocx)

    downloadReferenceAsDocX(sanitizedHtml, data.title ?? 'source', setDownloadLoading)
  }

  // Set the page title
  const caseTitle = data?.title ?? `${brandedAIFriendlyName} Reference Viewer`
  useTitle(caseTitle)

  // Fetch from the page so the badge surfaces regardless of which tab is active.
  const parentId = data?.parent_id
  const caseStatusState = useAppSelector((state: RootState) => (parentId ? state.citatorState.caseStatuses[parentId] : undefined))
  const caseStatus = caseStatusState?.data?.status

  useEffect(() => {
    if (!parentId || caseStatusState?.loading || caseStatusState?.data || caseStatusState?.error) return
    getCaseStatus(parentId)
  }, [parentId, caseStatusState?.loading, caseStatusState?.data, caseStatusState?.error])

  if (isUnsupportedIdType) {
    return (
      <div className={'grow flex flex-col h-full overflow-y-scroll pb-10 pt-8'}>
        <div className={'pl-2'}>
          {InlineTipError(
            'This reference link uses a legacy format that is no longer supported. Please search for this case using the citator to get an updated link.'
          )}
        </div>
      </div>
    )
  }

  return (
    <div className={'grow flex flex-col h-full overflow-y-scroll pb-4 pt-8'}>
      {isLoading && (
        <div className="flex justify-center items-center flex-1">
          <CircularProgressContinuousSized size={25} thickness={5} />
        </div>
      )}
      {isError && (
        <div className={'pl-2'}>
          {InlineTipError('Could not load this reference.')}
          <div>
            <button
              className={
                'items-center rounded-md bg-brand-500 border-[1px] border-brand-500 px-2 py-1 my-2 text-sm font-semibold text-white shadow-sm hover:bg-brand-400 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-400'
              }
              onClick={() => refetch()}
            >
              Retry
            </button>
          </div>
        </div>
      )}
      {data && (
        <div className="w-full max-w-3xl mx-auto flex flex-col h-full min-h-0">
          {/* Header */}
          <div className="pl-2 pb-4 flex flex-col gap-2">
            <div className="flex flex-col gap-1">
              <div className="flex items-center justify-between gap-4">
                <h1 className="text-xl font-bold leading-7 truncate text-brand-neutral-950">{data.title}</h1>
                {dataHtml && (
                  <Button
                    variant="tonal"
                    color="neutral"
                    size="sm"
                    trailingIcon={<FileDownload className="h-4 w-4" />}
                    loading={downloadLoading}
                    onClick={handleDownload}
                  >
                    Download
                  </Button>
                )}
              </div>
              {data.bb_citation && <p className="text-base leading-6 text-brand-neutral-950">{data.bb_citation}</p>}
            </div>
            <div className="flex items-center gap-3">
              {isDisplayableCaseStatus(caseStatus) && <CaseStatusInfoTag status={caseStatus} checkedLabelSize="default" />}
              <Link
                to={CITATOR_HELP_CENTER_URL}
                target="_blank"
                className="inline-flex items-center gap-0.5 text-xs font-medium text-brand-700 underline hover:text-brand-800"
              >
                Help center
                <OpenNew className="w-3 h-3" />
              </Link>
            </div>
          </div>

          {/* Tabs */}
          <StandaloneReferenceViewCaselawTabs
            metadata={data}
            sanitizedHtml={sanitizedHtml}
            isLoadingHtml={isLoadingHtml}
            isErrorHtml={isErrorHtml}
            refetchHtml={refetchHtml}
            analyticsContext={analyticsContext}
          />
        </div>
      )}
    </div>
  )
}
